/** Official Sports Club for Health (SCforH) initiative site. */
export const SCFORH_INFO_URL = "https://www.scforh.info/";

/** Sport Clubs for Health (SCforH) seed captured during club-admin profiling. */

export const EMPTY_SCFORH_SEED = {
  sportingAims: "",
  targetGroups: [],
  healthDimensions: [],
  applyAs: "",
  integration: "",
  currentOffer: "",
  likelyAppliedDimensions: [],
  underAppliedDimensions: [],
  growDimensions: [],
};

export const SCFORH_SPORTING_AIMS = [
  "competition",
  "recreation_health",
  "mixed",
];

export const SCFORH_TARGET_GROUPS = [
  "children",
  "adolescents",
  "adults",
  "seniors",
];

export const SCFORH_HEALTH_DIMENSIONS = ["physical", "mental", "social"];

export const SCFORH_APPLY_AS = ["philosophy", "programme", "both"];

export const SCFORH_INTEGRATION = ["into_coaching", "separate_sessions", "both"];

export const SCFORH_CURRENT_OFFER = ["mostly_competition", "mix", "strong_hepa"];

function applyAsFromIntegration(integration) {
  if (integration === "into_coaching") return "philosophy";
  if (integration === "separate_sessions") return "programme";
  if (integration === "both") return "both";
  return "";
}

function integrationFromApplyAs(applyAs) {
  if (applyAs === "philosophy") return "into_coaching";
  if (applyAs === "programme") return "separate_sessions";
  if (applyAs === "both") return "both";
  return "";
}

/** Keep applyAs ↔ integration mirrored (same question, two storage keys). */
export function syncScforhApplyIntegration(seed = {}) {
  const next = { ...(seed || {}) };
  if (next.integration && !next.applyAs) {
    next.applyAs = applyAsFromIntegration(next.integration);
  } else if (next.applyAs && !next.integration) {
    next.integration = integrationFromApplyAs(next.applyAs);
  } else if (next.integration && next.applyAs) {
    const mapped = applyAsFromIntegration(next.integration);
    if (mapped) next.applyAs = mapped;
  }
  return next;
}

export function isScforhSeedComplete(seed) {
  if (!seed || typeof seed !== "object") return false;
  const s = syncScforhApplyIntegration(seed);
  return Boolean(
    s.sportingAims &&
      Array.isArray(s.targetGroups) &&
      s.targetGroups.length > 0 &&
      Array.isArray(s.healthDimensions) &&
      s.healthDimensions.length > 0 &&
      (s.applyAs || s.integration) &&
      s.currentOffer
  );
}

/**
 * Infer which health dimensions are already in daily club life vs thinner.
 * Table tennis almost always trains the body; mental and social need design.
 * currentOffer (club status) outweighs sporting aims when both exist.
 */
export function inferHealthDimensionStatus(seed = {}, extras = {}) {
  const s = seed && typeof seed === "object" ? seed : {};
  const groups = (extras.groupNames || []).map((n) => String(n || "").toLowerCase());
  const likely = new Set(["physical"]);
  const under = new Set();
  let snapshotKey = "default";

  if (s.currentOffer === "mostly_competition" || (!s.currentOffer && s.sportingAims === "competition")) {
    snapshotKey = s.currentOffer === "mostly_competition" ? "mostly_competition" : "competition";
    under.add("mental");
    under.add("social");
  } else if (s.currentOffer === "strong_hepa") {
    snapshotKey = "strong_hepa";
    likely.add("social");
    under.add("mental");
  } else if (s.currentOffer === "mix" || (!s.currentOffer && s.sportingAims === "mixed")) {
    snapshotKey = s.currentOffer === "mix" ? "mix" : "mixed";
    under.add("mental");
    under.add("social");
  } else if (s.sportingAims === "recreation_health") {
    snapshotKey = "recreation_health";
    likely.add("social");
    under.add("mental");
  } else {
    under.add("mental");
    under.add("social");
  }

  const hasLeisure = groups.some((n) =>
    /loisir|leisure|sante|santé|health|recreat|social|famille|family/.test(n)
  );
  const hasCompet = groups.some((n) =>
    /compet|classement|ranking|elite|perf|match/.test(n)
  );
  if (hasLeisure) likely.add("social");
  if (hasCompet && !hasLeisure) {
    under.add("mental");
    under.add("social");
  }

  const tg = Array.isArray(s.targetGroups) ? s.targetGroups : [];
  if (tg.includes("seniors") || tg.includes("children") || tg.includes("adolescents")) {
    under.add("social");
  }

  for (const d of under) likely.delete(d);

  const order = ["physical", "mental", "social"];
  const underApplied = order.filter((d) => under.has(d));
  const likelyApplied = order.filter((d) => likely.has(d));
  const suggestedSelect = underApplied.length ? [...underApplied] : [...order];

  return {
    snapshotKey,
    likelyApplied,
    underApplied,
    suggestedSelect,
  };
}

/** True when any SCforH SMART letter is not complete. */
export function isClubScforhIncomplete(status) {
  const s = status && typeof status === "object" ? status : {};
  return ["S", "M", "A", "R", "T"].some((d) => s[d] !== "complete");
}
